import Box from "@mui/material/Box";
import { useWindowSize } from "@uidotdev/usehooks";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { PanelSize } from "react-resizable-panels";
import { Group, Panel, Separator, usePanelRef } from "react-resizable-panels";
import { Outlet, useLocation } from "react-router-dom";

import { FeedPanelContext } from "@/components/DriverView/ui/FeedPanelContext";
import MiddleColumn from "@/components/MiddleTableNavigation/MiddleColumn.tsx";
import LeftSidebar from "@/components/NewDesign/LeftSidebar.tsx";
import { isChannelFeedPath, isDriverFeedPath } from "@/components/NewDesign/newDesignUtils";
import InviteToWorkspaceModal from "@/components/Workspace/InviteToWorkspaceModal.tsx";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import UseWorkspaceMembershipAbilities from "@/hooks/useWorkspaceMembershipAbilities";
import SearchContainer from "../Search/SearchContainer";

const LEFT_PANEL_ID = "left-panel";
const RIGHT_PANEL_ID = "right-panel";

export default function NewDesignLayout({ children }: { children?: React.ReactNode }) {
  const size = useWindowSize();
  const parentRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = usePanelRef();
  const [columns, setColumns] = useSafeLocalStorage("panel-columns", {
    left: 70,
    right: 30,
  });
  const [collapseRightPanel, setCollapseRightPanel] = useState(false);
  const [panelSizes, setPanelSizes] = useState<{ left: number; right: number }>(columns);
  const [middleColInPixels, setMiddleColInPixels] = useState<number>(500);

  const location = useLocation();
  const isDriverView = isDriverFeedPath(location.pathname);

  UseWorkspaceMembershipAbilities();

  const getResidedWidth = (): number => {
    const sidebarWidth = 60;
    const totalWidth = parentRef?.current?.offsetWidth;
    return totalWidth - sidebarWidth;
  };

  const determineMiddleColWidth = (left?: number): number => {
    const leftValue = left ?? panelSizes.left;
    const totalWidth = parentRef?.current?.offsetWidth;
    const leftPanelWidth = (leftValue / 100) * totalWidth;
    const sidebarWidth = 60;
    const value = leftPanelWidth - sidebarWidth;
    setMiddleColInPixels(() => value);
    return value;
  };

  const setPanelSizeValues = (data: Record<string, number>) => {
    const left = data[LEFT_PANEL_ID];
    const right = data[RIGHT_PANEL_ID];
    if (left == null || right == null) return;

    setPanelSizes({ left, right });
    determineMiddleColWidth(left);
    if (right > 0) {
      setColumns({ left, right });
    }
  };

  const handleRightPanelResize = (panelSize: PanelSize) => {
    setCollapseRightPanel(panelSize.asPercentage === 0);
  };

  useEffect(() => {
    if (isDriverView) return;
    const value = collapseRightPanel ? getResidedWidth() : determineMiddleColWidth();
    setMiddleColInPixels(value);
  }, [size, collapseRightPanel, isDriverView]);

  const closeRightPanel = () => {
    const panel = rightPanelRef.current;
    if (!panel || panel.isCollapsed()) return;
    setColumns({ left: panelSizes.left, right: panelSizes.right });
    panel.collapse();
  };

  const openRightPanel = useCallback(() => {
    const panel = rightPanelRef.current;
    if (panel?.isCollapsed()) panel.expand();
  }, []);

  useEffect(() => {
    if (isDriverView) return;
    if (isChannelFeedPath(location.pathname)) {
      openRightPanel();
    }
  }, [location.pathname, isDriverView, openRightPanel]);

  const showLayoutPanels = !children && !isDriverView;

  return (
    <Box
      ref={parentRef}
      className="new-design"
      id="newDesignFlag"
      sx={{ bgcolor: "#000", height: "100vh", overflow: "hidden", display: "flex" }}
    >
      {showLayoutPanels ? (
        <FeedPanelContext.Provider value={{ closePanel: closeRightPanel, openPanel: openRightPanel }}>
          <Group
            defaultLayout={{ [LEFT_PANEL_ID]: columns.left, [RIGHT_PANEL_ID]: columns.right }}
            onLayoutChanged={setPanelSizeValues}
          >
            <Panel id={LEFT_PANEL_ID}>
              <Box sx={{ display: "flex", height: "100%" }}>
                <Box sx={{ width: "60px" }}>
                  <LeftSidebar />
                </Box>
                <Box sx={{ flexGrow: 1, position: "relative", overflow: "hidden" }}>
                  <div style={{ width: middleColInPixels ? `${middleColInPixels}px` : "100%" }}>
                    <MiddleColumn />
                  </div>
                </Box>
              </Box>
            </Panel>
            <Separator className="separator" style={collapseRightPanel ? { display: "none" } : undefined} />
            <Panel
              id={RIGHT_PANEL_ID}
              maxSize="50"
              minSize="20"
              collapsible
              collapsedSize={0}
              panelRef={rightPanelRef}
              onResize={handleRightPanelResize}
              style={{ background: "#000" }}
            >
              <Outlet />
            </Panel>
          </Group>
        </FeedPanelContext.Provider>
      ) : (
        <Box sx={{ display: "flex", height: "100%", width: "100%" }}>
          <Box sx={{ width: "60px" }}>
            <LeftSidebar />
          </Box>
          <Box sx={{ flexGrow: 1, position: "relative", overflow: "hidden" }}>{children ?? <MiddleColumn />}</Box>
        </Box>
      )}
      <SearchContainer />
      <InviteToWorkspaceModal />
    </Box>
  );
}
